// ============================================================
// Kategori Produk Kasir — daftar & CRUD modal + ikon maskot
// ============================================================

import axios from '../../config/axios.js';
import Alpine from 'alpinejs';

function createKasirProductCategories() {
  return {
    hydrated: false,
    loading: false,
    saving: false,
    loadingIcons: false,
    uploadingIcon: false,
    categories: [],
    maskotIcons: [],
    defaultIcon: 'default.png',
    iconSearch: '',
    search: '',
    filterStatus: '',
    showModal: false,
    editing: null,
    form: {
      name: '',
      slug: '',
      icon: '',
      description: '',
      sort_order: 0,
      is_active: true,
    },

    init() {
      try {
        const raw = this.$el.getAttribute('data-config');
        if (raw) {
          const cfg = JSON.parse(raw);
          this.categories = cfg.categories || [];
          this.maskotIcons = cfg.maskotIcons || [];
          this.defaultIcon = cfg.defaultIcon || 'default.png';
        }
      } catch (e) {
        console.error('kasirProductCategories: gagal memuat config', e);
      }
      this.hydrated = true;
      if (!this.maskotIcons.length) {
        this.loadMaskotIcons();
      }
    },

    async loadMaskotIcons() {
      this.loadingIcons = true;
      try {
        const { data } = await axios.get('/api/v1/kasir/product-categories/meta/maskot-icons');
        const payload = data.data || {};
        this.maskotIcons = payload.icons || [];
        if (payload.default_icon) this.defaultIcon = payload.default_icon;
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal memuat galeri ikon maskot.', 'error');
      } finally {
        this.loadingIcons = false;
      }
    },

    filteredMaskotIcons() {
      const q = (this.iconSearch || '').trim().toLowerCase();
      if (!q) return this.maskotIcons;
      return this.maskotIcons.filter((opt) => {
        const hay = `${opt.file || ''} ${opt.label || ''}`.toLowerCase();
        return hay.includes(q);
      });
    },

    selectedIconPreview() {
      const file = this.form.icon || this.defaultIcon;
      const found = this.maskotIcons.find((o) => o.file === file);
      if (found?.url) return found.url;
      if (file && String(file).endsWith('.png')) {
        return '/images/maskot/web/48/' + file;
      }
      return '/images/maskot/web/48/' + (this.defaultIcon || 'default.png');
    },

    async uploadIcon(event) {
      const input = event?.target;
      const file = input?.files?.[0];
      if (!file) return;
      if (!file.type.includes('png') && !file.name.toLowerCase().endsWith('.png')) {
        window.toast('Hanya file PNG yang didukung.', 'warning');
        input.value = '';
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        window.toast('Ukuran maksimal 2MB.', 'warning');
        input.value = '';
        return;
      }

      this.uploadingIcon = true;
      try {
        const fd = new FormData();
        fd.append('icon', file);
        fd.append('name', file.name.replace(/\.png$/i, ''));
        const { data } = await axios.post(
          '/api/v1/kasir/product-categories/meta/maskot-icons',
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        const stored = data.data;
        if (stored?.file) {
          this.maskotIcons = [
            stored,
            ...this.maskotIcons.filter((o) => o.file !== stored.file),
          ];
          this.form.icon = stored.file;
          this.iconSearch = '';
          window.toast('Ikon maskot diupload & dipilih.', 'success');
        }
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal upload ikon.', 'error');
      } finally {
        this.uploadingIcon = false;
        if (input) input.value = '';
      }
    },

    async fetch() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/v1/kasir/product-categories', {
          params: { search: this.search, status: this.filterStatus },
        });
        this.categories = data.data || [];
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal memuat kategori.', 'error');
      } finally {
        this.loading = false;
      }
    },

    openForm(cat = null) {
      this.editing = cat;
      this.iconSearch = '';
      if (cat) {
        this.form = {
          name: cat.name,
          slug: cat.slug,
          icon: cat.icon || '',
          description: cat.description || '',
          sort_order: cat.sort_order ?? 0,
          is_active: !!cat.is_active,
        };
      } else {
        this.form = {
          name: '',
          slug: '',
          icon: this.defaultIcon,
          description: '',
          sort_order: 0,
          is_active: true,
        };
      }
      this.showModal = true;
      if (!this.maskotIcons.length) this.loadMaskotIcons();
    },

    async save() {
      if (!this.form.name?.trim()) {
        window.toast('Nama kategori wajib diisi.', 'warning');
        return;
      }

      this.saving = true;
      const payload = {
        name: this.form.name.trim(),
        slug: this.form.slug?.trim() || null,
        icon: this.form.icon?.trim() || null,
        description: this.form.description?.trim() || null,
        sort_order: Number(this.form.sort_order) || 0,
        is_active: !!this.form.is_active,
      };

      try {
        if (this.editing) {
          await axios.put('/api/v1/kasir/product-categories/' + this.editing.id, payload);
          window.toast('Kategori diperbarui.', 'success');
        } else {
          await axios.post('/api/v1/kasir/product-categories', payload);
          window.toast('Kategori ditambahkan.', 'success');
        }
        this.showModal = false;
        await this.fetch();
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal menyimpan.', 'error');
      } finally {
        this.saving = false;
      }
    },

    iconUrl(cat) {
      if (cat?.icon_url) return cat.icon_url;
      const file = cat?.icon;
      if (file && file.endsWith('.png')) {
        return '/images/maskot/web/48/' + file;
      }
      return '/images/maskot/web/48/default.png';
    },

    async remove(cat) {
      const ok = await window.confirmDialog({
        title: 'Hapus Kategori?',
        message: `Hapus kategori "${cat.name}"? Tindakan ini tidak dapat dibatalkan.`,
        confirmText: 'Ya, Hapus',
        cancelText: 'Batal',
        variant: 'danger',
      });
      if (!ok) return;

      try {
        await axios.delete('/api/v1/kasir/product-categories/' + cat.id);
        window.toast('Kategori dihapus.', 'success');
        await this.fetch();
      } catch (e) {
        window.toast(e.response?.data?.message || 'Gagal menghapus.', 'error');
      }
    },
  };
}

Alpine.data('kasirProductCategories', createKasirProductCategories);
